import { useI18n } from 'vue-i18n';

/**
 * THE way a shared component reads its own colocated strings (SKILL.md rule 11).
 *
 * ```js
 * import { useComponentI18n } from '@rdrive/i18n';
 * import messages from './messages/index.js';
 * const { t } = useComponentI18n(messages);   // templates call t(), never $t
 * ```
 *
 * **Local scope.** vue-i18n's `getScope(options, componentOptions)` returns `'local'` whenever
 * the options object is non-empty and `useScope` is unset, so passing `messages` is by itself
 * what makes the composer component-local. The keys resolve inside the component and are
 * invisible to the app's bundle — which is what keeps the component liftable into another
 * dashboard with its strings attached.
 *
 * **`inheritLocale` and `fallbackRoot` keep their defaults (both `true`)**, deliberately:
 *   - `inheritLocale` is why the component follows the app's current locale, including a
 *     runtime switch, with nothing to wire.
 *   - `fallbackRoot` is why a key this folder does not carry still resolves through the root
 *     composer. Two components rely on that — `StackedHorizontalBarChart` calls
 *     `t(navigationConfig[].titleKey)` with an APP key on purpose, and `WelcomeDialog`'s
 *     `loaded` used to live nowhere at all.
 *
 * **Why the warn flags are ASSIGNED, not passed.** In vue-i18n 10 `createComposer` does
 * `let _missingWarn = __root ? __root.missingWarn : (isBoolean(options.missingWarn) ? … : true)`
 * — and the same for `fallbackWarn`/`fallbackRoot`. A component-local composer ALWAYS has a
 * root, so those three constructor options are silently ignored and inherited from the global
 * composer instead. Only the setters take effect (they also update the composer's core
 * context), hence the two assignments below. Measured on the base chart: six `[intlify]` lines
 * per render became zero. A key missing from *both* scopes still warns — from the app's root
 * composer — so the app keeps its diagnostics.
 *
 * @param {Record<string, object>} messages `{ [locale]: { key: value } }` — normally the
 *   default export of the component's `./messages/index.js` barrel.
 * @returns {import('vue-i18n').Composer} The local composer. Destructure `t` (and `rt`/`tm` if
 *   needed); it is returned whole so a caller can still read `locale` or re-set a flag.
 */
export function useComponentI18n(messages) {
  const i18n = useI18n({ messages });
  i18n.missingWarn = false;
  i18n.fallbackWarn = false;
  return i18n;
}
